<script setup lang="ts">
import PrimeDatePicker from 'primevue/datepicker'

interface Props {
  /** Bound value. `null` clears the input. */
  modelValue?: Date | null
  /** PrimeVue date format token (see PrimeVue DatePicker docs). */
  dateFormat?: string
  /** Empty-state placeholder text. */
  placeholder?: string
  /** Disable user interaction. */
  disabled?: boolean
  /** Render the value but block editing — greyed read-only look. */
  readonly?: boolean
  /** Apply the invalid-state styling. */
  invalid?: boolean
  /** Render the calendar trigger icon inside the input. */
  showIcon?: boolean
  /** Earliest selectable date (inclusive). */
  minDate?: Date
  /** Latest selectable date (inclusive). */
  maxDate?: Date
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  dateFormat: 'dd-mm-yy',
  showIcon: true,
  disabled: false,
  readonly: false,
  invalid: false,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: Date | null): void
}>()
</script>

<template>
  <PrimeDatePicker
    :modelValue="props.modelValue"
    :dateFormat="props.dateFormat"
    :placeholder="props.placeholder"
    :disabled="props.disabled"
    :readonly="props.readonly"
    :invalid="props.invalid"
    :showIcon="props.showIcon"
    :minDate="props.minDate"
    :maxDate="props.maxDate"
    iconDisplay="input"
    class="app-datepicker"
    inputClass="app-datepicker-field"
    @update:modelValue="emit('update:modelValue', $event as Date | null)"
  />
</template>

<style>
/* Unscoped on purpose -- targets PrimeVue inner / teleported DOM that scoped CSS cannot reach. */
.app-datepicker {
  width: 100%;
}

.app-datepicker-field {
  width: 100%;
  height: 42px;
  border-radius: 6px;
  border: 1px solid var(--p-surface-300);
  background: var(--p-surface-0);
  font-size: 0.9375rem;
  color: var(--p-text-color);
  padding: 0 0.75rem;
  font-family: inherit;
}

.app-datepicker-field:not(:disabled):not([readonly]):hover {
  border-color: var(--p-primary-300);
}

.app-datepicker-field:not([readonly]):focus {
  border-color: var(--p-primary-400);
  box-shadow: 0 0 0 1px var(--p-primary-400);
  outline: none;
}

/* Read-only mirrors the disabled look — greyed surface, muted text, no caret. */
.app-datepicker-field[readonly] {
  background: var(--p-surface-100);
  color: var(--p-text-muted-color);
  border-color: var(--p-surface-200);
  cursor: not-allowed;
}

.app-datepicker .p-datepicker-trigger {
  color: var(--p-text-muted-color);
}
</style>
